import { useState } from 'react'
import {
  Avatar,
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Icon,
  IconButton,
  Input,
  SimpleGrid,
  Text,
  Tooltip,
  VStack,
  useToast,
} from '@chakra-ui/react'
import { FiCheck, FiEdit2, FiGlobe, FiLock, FiMail, FiUser, FiX, FiBriefcase, FiCalendar } from 'react-icons/fi'
import { useAuth } from '@/contexts/AuthContext'
import { useAppTranslation, useLanguage } from '@/contexts/LanguageContext'
import { RoleBadge } from '@/components/Common'
import { LanguageSwitcher } from '@/components/Common/LanguageSwitcher'
import apiService from '@/services/api'

export default function ProfileTab() {
  const { t, formatDate } = useAppTranslation(['common', 'auth'])
  const { user, organization, refreshUser } = useAuth()
  const { isLanguageLocked } = useLanguage()
  const toast = useToast()

  const [isEditingName, setIsEditingName] = useState(false)
  const [nameDraft, setNameDraft] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  if (!user) return null

  const org = organization ?? user.organization

  const startEditingName = () => {
    setNameDraft(user.name)
    setIsEditingName(true)
  }

  const hasNameChanged = nameDraft.trim() !== '' && nameDraft.trim() !== user.name

  const saveName = async () => {
    const name = nameDraft.trim()
    if (!name || name === user.name) return

    setIsSaving(true)
    try {
      await apiService.updateMe({ name })
      await refreshUser()
      setIsEditingName(false)
      toast({
        title: t('profile.profileUpdated', { ns: 'auth' }),
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch {
      toast({
        title: t('account.profile.updateFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <VStack spacing={8} align="stretch" maxW="3xl">
      {/* Identity header */}
      <HStack spacing={5} align="center">
        <Avatar size="xl" name={user.name} bg="brand.500" color="white" />
        <VStack align="start" spacing={1}>
          <HStack spacing={3}>
            <Heading size="md" color="gray.800">
              {user.name}
            </Heading>
            <RoleBadge role={user.role} />
          </HStack>
          <HStack spacing={2} color="gray.600">
            <Icon as={FiMail} boxSize={4} />
            <Text fontSize="sm">{user.email}</Text>
          </HStack>
          {org && (
            <HStack spacing={2} color="gray.600">
              <Icon as={FiBriefcase} boxSize={4} />
              <Text fontSize="sm">{org.name}</Text>
            </HStack>
          )}
        </VStack>
      </HStack>

      <Divider />

      {/* Personal information */}
      <Box>
        <HStack spacing={2} mb={4}>
          <Icon as={FiUser} color="brand.500" boxSize={4} />
          <Heading size="sm">{t('profile.personalInfo', { ns: 'auth' })}</Heading>
        </HStack>

        <VStack spacing={4} align="stretch">
          <FormControl>
            <FormLabel fontSize="sm" color="gray.600">
              {t('account.profile.name')}
            </FormLabel>
            {isEditingName ? (
              <HStack>
                <Input
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveName()
                    if (e.key === 'Escape') setIsEditingName(false)
                  }}
                />
                <IconButton
                  aria-label={t('account.profile.save')}
                  icon={<FiCheck />}
                  colorScheme="green"
                  onClick={saveName}
                  isLoading={isSaving}
                  isDisabled={!hasNameChanged}
                />
                <IconButton
                  aria-label={t('account.profile.cancel')}
                  icon={<FiX />}
                  variant="ghost"
                  onClick={() => setIsEditingName(false)}
                  isDisabled={isSaving}
                />
              </HStack>
            ) : (
              <HStack justify="space-between">
                <Text fontWeight="medium">{user.name}</Text>
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={<FiEdit2 />}
                  onClick={startEditingName}
                >
                  {t('account.profile.edit')}
                </Button>
              </HStack>
            )}
          </FormControl>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <Box>
              <Text fontSize="sm" color="gray.600" mb={1}>
                {t('account.profile.email')}
              </Text>
              <Text fontWeight="medium">{user.email}</Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.600" mb={1}>
                {t('account.profile.role')}
              </Text>
              <RoleBadge role={user.role} />
            </Box>
            {org && (
              <Box>
                <Text fontSize="sm" color="gray.600" mb={1}>
                  {t('account.profile.organization')}
                </Text>
                <Text fontWeight="medium">{org.name}</Text>
              </Box>
            )}
            <Box>
              <HStack spacing={1} mb={1}>
                <Icon as={FiCalendar} boxSize={3.5} color="gray.500" />
                <Text fontSize="sm" color="gray.600">
                  {t('account.profile.memberSince')}
                </Text>
              </HStack>
              <Text fontWeight="medium">{formatDate(new Date(user.created_at))}</Text>
            </Box>
          </SimpleGrid>
        </VStack>
      </Box>

      <Divider />

      {/* Preferences */}
      <Box>
        <HStack spacing={2} mb={4}>
          <Icon as={FiGlobe} color="brand.500" boxSize={4} />
          <Heading size="sm">{t('profile.preferences', { ns: 'auth' })}</Heading>
        </HStack>

        <FormControl>
          <FormLabel fontSize="sm" color="gray.600">
            {t('account.profile.language')}
          </FormLabel>
          {isLanguageLocked ? (
            <Tooltip label={t('account.profile.languageLocked')}>
              <HStack spacing={2} color="gray.500">
                <Icon as={FiLock} boxSize={4} />
                <Text fontSize="sm">{t('account.profile.languageLocked')}</Text>
              </HStack>
            </Tooltip>
          ) : (
            <LanguageSwitcher variant="full" size="sm" />
          )}
        </FormControl>
      </Box>
    </VStack>
  )
}
